import { CareerId } from '@common/const/catalog/career';
import { CatalogItemCountingsForCareerLinkingResponseItem } from '@common/model/catalog/item/countings.request';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ClientWebPageProps } from '@common/model/meta/clientWebPageProps';
import { ClientWebRouteMetaViewModel } from '@common/model/meta/clientWebRouteMetaViewModel';
import { coreRouterClientWebRoutesMetaByPageApi, useClientWebResolver, useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { useCurrentCity } from '@cp/entities/location';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';
import React from 'react';
import { coreCatalogApi } from '@cp/entities/catalog';
// import { useRouter } from '@cp/shared/router';
// import { CATALOG_MIN_ITEMS_COUNT_FOR_INDEXING } from '@common/const/catalog';

type ISpecializationLink = { key: number; label: string; payload: CatalogItemCountingsForCareerLinkingResponseItem } & (
  | { onClick: () => void }
  | { href: string }
);

export const useSubcatalogSpecializationLinks = (props: { careerId: CareerId; itemType: CatalogItemType }) => {
  // const router = useRouter();
  const { careerId, itemType } = props;
  const { currentCity } = useCurrentCity();
  const { routeId } = useClientWebRouteParams();
  const resolver = useClientWebResolver();

  const { data: careerRoutesMeta, isLoading: isLoadingCareerRoutesMeta } =
    coreRouterClientWebRoutesMetaByPageApi.useGetClientWebRoutesMetaByPageQuery({
      pageId: ClientWebPage.Catalog,
      parameters: ['breadcrumb'],
    });

  const { data: catalogItemsBatchedCountings, isLoading: isLoadingCatalogItemsBatchedCount } =
    coreCatalogApi.endpoints.getItemsCountingForCareerLinking.useQuery({
      type: itemType,
      careerId,
      cityId: currentCity.id,
      exceptRouteId: routeId as ApiServerEntityIDType,
    });

  const isLoading = isLoadingCareerRoutesMeta || isLoadingCatalogItemsBatchedCount;

  const links = React.useMemo(() => {
    if (!careerRoutesMeta || !catalogItemsBatchedCountings || isLoading) {
      return [];
    }

    const routesMetaDict = careerRoutesMeta?.reduce(
      (acc, routeMeta) => {
        if (routeMeta.routeId) {
          acc[routeMeta.routeId] = routeMeta;
        }
        return acc;
      },
      {} as Record<ApiServerEntityIDType, Pick<ClientWebRouteMetaViewModel, 'breadcrumb'>>,
    );

    return (
      catalogItemsBatchedCountings.map<ISpecializationLink>((batchedCounting) => {
        const { type, careerId: cId, routeId: rId, filter } = batchedCounting;
        const href = resolver.resolve({
          page: ClientWebPage.Catalog,
          pageProps: { catalogType: type, careerId: cId, filter } as ClientWebPageProps[ClientWebPage.Catalog],
          cityAlias: currentCity.alias,
        });

        return {
          key: rId,
          label: String(routesMetaDict[rId]?.breadcrumb).toLocaleLowerCase(),
          payload: batchedCounting,
          href: String(href),
          // ...(hidden
          //   ? {
          //       onClick: () => router.navigate(href),
          //     }
          //   : {
          //       href: String(href),
          //     }),
        };
      }) ?? []
    );
  }, [careerRoutesMeta, catalogItemsBatchedCountings, isLoading, resolver, currentCity.alias]);

  return { links, isLoading };
};
